import type { ILanguage, IProjectLanguageData } from "../../../model/model";
import type { NodeIssue } from "./graphValidator";
import type { Node } from "./nodeTypes";

//
// Per-language validation of a node's project-language-data (PLD) rows.
//
// Mirrors the Unity editor validator (ValidateLanguageData /
// ValidateLanguageDataKeys): for every content type that carries translatable
// values, each enabled language must have a PLD row, and that row must carry a
// value for every field the content itself has filled. Field names below map
// PLD keys to their content counterpart, transposed from Unity's
// LanguageDataKeyMap to the web model names.
//
// Severities:
// ➡︎ error   = duplicate PLD rows or a -1 media id — both break the runtime.
// ➡︎ warning = missing row / untranslated fields — plays with holes in that
//             language.
//

// [pldKeys (aliases, first found wins), contentKey]
type KeyPair = [string[], string];

const LANGUAGE_KEY_MAP: Record<string, KeyPair[]> = {
  // Backend writes scene_media_id (see content-scene createScene controller);
  // the frontend model declares media_scene_id — accept both.
  "content-scene": [[["scene_media_id", "media_scene_id"], "source_media"]],
  "content-audio": [[["media_audio_id"], "source_audio"]],
  "content-avatar-audio-player": [[["media_audio_id"], "source_audio"]],
  "content-tuto": [[["media_image_id"], "source_logo"]],
  "content-tuto2": [[["media_image_id"], "source_logo"]],
  "content-video-2-d": [[["media_video_id"], "source_media"]],
  "content-pdf-viewer": [[["media_pdf_id"], "source_pdf"]],
  "content-onboarding": [
    [["text"], "text"],
    [["media_image_id"], "source_image_2d"],
  ],
  "content-thumbnail": [
    [["text"], "text"],
    [["gazed_text"], "gazed_text"],
    [["media_image_id"], "source_image"],
  ],
  "content-text": [[["text"], "text"]],
  "content-hotspot-scene-image": [[["text"], "text"]],
  "content-hotspot-scene-video": [[["text"], "text"]],
  "content-clean": [
    [["title"], "title"],
    [["text"], "entitled"],
    [["media_answer1_id"], "default_image_1"],
    [["media_answer2_id"], "default_image_2"],
  ],
  "content-fire-extinguisher": [
    [["title"], "title"],
    [["text"], "text_instructions"],
    [["text_fail"], "text_loose"],
    [["explanation_true"], "title_win"],
    [["explanation_false"], "title_loose"],
    [["intro_text"], "instructions_panel_text"],
    [["end_text"], "text_win"],
  ],
  "content-quizz-1": [
    [["question"], "question_text"],
    [["answer1"], "answer_1_text"],
    [["answer2"], "answer_2_text"],
    [["answer3"], "answer_3_text"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
  ],
  "content-quizz-2": [
    [["question"], "question_text"],
    [["media_answer1_id"], "answer_1_image"],
    [["media_answer2_id"], "answer_2_image"],
    [["media_answer3_id"], "answer_3_image"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
  ],
  "content-quizz-3": [
    [["question"], "question_text"],
    [["list_answers"], "list_answers"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
    [["media_audio_success_id"], "source_media_win_sound"],
    [["media_audio_fail_id"], "source_media_loose_sound"],
  ],
  "content-yes-no-question": [
    [["question"], "question_text"],
    [["answer1"], "yes_button_text"],
    [["answer2"], "no_button_text"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
    [["media_audio_success_id"], "source_media_win_sound"],
    [["media_audio_fail_id"], "source_media_loose_sound"],
  ],
  "content-quizz-sort": [
    [["text"], "question"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
    [["list_answer_quizz_sort_data"], "answers"],
  ],
  "content-quizz-image-multiple": [
    [["question"], "question_text"],
    [["list_answers"], "list_answers"],
    [["explanation_true"], "explanation_true_text"],
    [["explanation_false"], "explanation_false_text"],
    [["media_audio_success_id"], "source_media_win_sound"],
    [["media_audio_fail_id"], "source_media_loose_sound"],
  ],
  "content-random-quizz": [
    [["end_text"], "end_text"],
    [["list_question_random_quizz_data"], "questions"],
    [["media_audio_success_id"], "source_media_win_sound"],
    [["media_audio_fail_id"], "source_media_loose_sound"],
  ],
  "content-speech-to-check": [
    [["title"], "title"],
    [["text_fail"], "fail_text"],
    [["intro_text"], "intro_text"],
    [["end_text"], "end_text"],
    [["list_keyword_data"], "keywords_info"],
  ],
  "content-zone-detect": [
    [["title"], "title"],
    [["text"], "entitled"],
    [["intro_text"], "instruction"],
    [["list_hotspot_data"], "hotspot_info"],
  ],
  "content-zone-sort": [
    [["title"], "title"],
    [["text"], "entitled"],
    [["list_hotspot_zone_sort_data"], "hotspot_info"],
  ],
  "content-photo-catch": [
    [["text"], "text_instruction"],
    [["text_success"], "text_success"],
    [["text_fail"], "text_fail"],
  ],
  "content-danger-catch": [
    [["text_success"], "text_success"],
    [["text_fail"], "text_fail"],
    [["media_audio_success_id"], "audio_success"],
    [["media_audio_fail_id"], "audio_fail"],
    [["media_answer1_id"], "image_success"],
    [["media_answer2_id"], "image_fail"],
    [["media_answer3_id"], "audio_catch"],
  ],
  "content-element-catch": [
    [["intro_text"], "text_instructions"],
    [["end_text"], "text_result"],
    [["list_element_catch_element_data"], "element_list"],
  ],
};

// Empty = null, blank string, unset id (0 or the -1 "no media" sentinel used
// throughout the web DB), or empty array (possibly JSON-encoded). Unknown
// shapes count as filled — never cry wolf.
const isEmptyValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return true;
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) && parsed.length === 0;
      } catch {
        return false;
      }
    }
    return false;
  }
  if (typeof value === "number") return value <= 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

// Translation-only projections. Some list fields' PLD copy keeps only the
// translatable texts (see backend pldElementTransforms.js): quizz-image-
// multiple answers keep each answer's `text` — imageId/is_right live on the
// content, shared across ALL languages — and element-catch elements keep only
// name/description (wrapped under `element` on the PLD side). For these
// fields "filled" must mean "has at least one non-blank translatable text":
// an image-only answer list has nothing to translate, so its per-language
// copy being empty is the normal state, not a hole.
const TEXT_PROJECTIONS: Record<string, Record<string, (entry: any) => unknown[]>> = {
  "content-quizz-image-multiple": {
    // Content side: [{ text, imageId, is_right }]. PLD side: plain string[].
    list_answers: (a) => [typeof a === "string" ? a : a?.text],
  },
  "content-element-catch": {
    // Content side: [{ name, description, ...numerics }]. PLD side wraps each
    // element under `element` with the numerics zeroed.
    element_list: (e) => {
      const el = e?.element ?? e;
      return [el?.name, el?.description];
    },
  },
};

const toArray = (value: unknown): unknown[] | null => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim().startsWith("[")) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const projectTranslatableTexts = (
  modelName: string,
  contentKey: string,
  value: unknown,
): unknown => {
  const projector = TEXT_PROJECTIONS[modelName]?.[contentKey];
  if (!projector) return value;
  const arr = toArray(value);
  if (!arr) return value;
  return arr.flatMap(projector).filter((t) => typeof t === "string" && t.trim().length > 0);
};

const readPldValue = (pld: IProjectLanguageData, aliases: string[]): unknown => {
  for (const key of aliases) {
    const value = (pld as any)[key];
    if (value !== undefined) return value;
  }
  return undefined;
};

export const collectNodeLanguageIssues = (
  node: Node,
  pldsForContent: IProjectLanguageData[],
  enabledLanguages: ILanguage[],
): NodeIssue[] => {
  const keyPairs = LANGUAGE_KEY_MAP[node.modelName];
  if (!keyPairs || enabledLanguages.length === 0) return [];

  const issues: NodeIssue[] = [];
  const details: any = node.details ?? {};

  for (const language of enabledLanguages) {
    const rows = pldsForContent.filter((pld) => Number(pld.language) === Number(language.id));
    const languageName = language.name || language.ISO_639_1 || String(language.id);

    // Missing PLD row: only meaningful on multilingual projects — a fresh
    // single-language module always has its row created with the content.
    if (rows.length === 0) {
      if (enabledLanguages.length > 1) {
        issues.push({
          severity: "warning",
          messageKey: "missingTranslation",
          messageParams: { language: languageName },
        });
      }
      continue;
    }

    // Duplicate rows for the same content+language: runtime behavior is
    // undefined (which copy plays?), same as the Unity validator flags it.
    if (rows.length > 1) {
      issues.push({
        severity: "error",
        messageKey: "duplicateTranslation",
        messageParams: { language: languageName },
      });
    }

    const row = rows[0];
    const untranslatedKeys: string[] = [];
    for (const [aliases, contentKey] of keyPairs) {
      // Both sides go through the translation-only projection so a list whose
      // translatable texts are all blank counts as empty on either end.
      const pldValue = projectTranslatableTexts(
        node.modelName,
        contentKey,
        readPldValue(row, aliases),
      );
      const contentValue = projectTranslatableTexts(
        node.modelName,
        contentKey,
        details[contentKey],
      );
      // Only flag a hole the content itself has filled: an optional field left
      // empty everywhere is a choice, not a missing translation. NB: a -1
      // media id is the DB's "no media" sentinel, i.e. just empty.
      if (!isEmptyValue(contentValue) && isEmptyValue(pldValue)) {
        untranslatedKeys.push(contentKey);
      }
    }

    if (untranslatedKeys.length > 0) {
      issues.push({
        severity: "warning",
        messageKey: "untranslatedFields",
        messageParams: { count: untranslatedKeys.length, language: languageName },
        fieldKeys: untranslatedKeys,
      });
    }
  }

  return issues;
};
